// AssetBar - 下部のアセット選択バー
// キャラ・ゴール + スロット + 一覧ボタン

import { useDrag } from "react-dnd";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useRef, useMemo, useState, useEffect, PointerEvent } from "react";
import AppsIcon from "@mui/icons-material/Apps";
import { RootState } from "app/store";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { AssetModel, AssetModelType } from "common/model";
import { actions, selectAllMapModel } from "features/builder/slice";
import { AssetListModal } from "./AssetListModal";

// アセットアイテムコンポーネント
const AssetItem = ({
  asset,
  selected,
  disabled,
  onSelect,
}: {
  asset: AssetModel;
  selected: boolean;
  disabled?: boolean;
  onSelect: (asset: AssetModel) => void;
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: asset.type,
    item: asset,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  useEffect(() => {
    if (isDragging) {
      setIsPressed(false);
      if (onSelect && !disabled) {
        onSelect(asset);
      }
    }
  }, [isDragging]);

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const handlePointerDown = () => setIsPressed(true);
  const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isPressed && onSelect && !disabled) {
      onSelect(asset);
      setIsPressed(false);
    }
  };

  return (
    <div
      ref={(node) => {
        if (onSelect && !disabled && drag) drag(node);
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      className={`flex-col-el flex-center w-full h-full rounded-lg cursor-pointer transition-all duration-150 active:scale-95
        ${selected ? "ring-2 ring-blue-400 bg-blue-50" : ""}
        ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-white/50"}
      `}
    >
      <img
        draggable="false"
        className="w-full h-full object-contain select-none p-1"
        src={`${Constants.assetHost}/assets/models/${asset.name}.png`}
      />
    </div>
  );
};

// 空のスロット
const EmptySlot = ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
  <div
    onClick={onClick}
    className={`flex-col-el flex-center w-full h-full rounded-lg cursor-pointer transition-all duration-150 active:scale-95 border-2 border-dashed
      ${selected ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-gray-400 hover:bg-white/30"}
    `}
  >
    <span className="text-2xl text-gray-400">+</span>
  </div>
);

export const AssetBar = () => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const allMapModel = useSelector(selectAllMapModel);
  const selectedSlotIndex = useSelector((state: RootState) => state.builder.selectedSlotIndex);
  const selectedAssetModel = useSelector((state: RootState) => state.builder.selectedAssetModel);
  const showAssetList = useSelector((state: RootState) => state.builder.actionMenu.assetList);
  const slot = useSelector((state: RootState) => state.builder.slot);

  const [itemSize, setItemSize] = useState(64);

  // レスポンシブサイズ計算
  useEffect(() => {
    const computeSize = () => {
      const vh = window.innerHeight;
      const size = Math.max(48, Math.min(72, Math.floor(vh * 0.1)));
      setItemSize(size);
    };
    computeSize();
    window.addEventListener("resize", computeSize);
    return () => window.removeEventListener("resize", computeSize);
  }, []);

  // 固定アセット
  const character = useMemo<AssetModel>(
    () => ({
      type: AssetModelType.CHARACTER,
      name: "character_duck",
      filename: "character_duck.glb",
      rotation: true,
      offset: { x: 0, y: -0.4, z: 0 },
      scaling: { x: 1.2, y: 1.2, z: 1.2 },
    }),
    [],
  );

  const goal = useMemo<AssetModel>(
    () => ({
      type: AssetModelType.GOAL,
      name: "flag_teamRed",
      filename: "flag_teamRed.gltf.glb",
    }),
    [],
  );

  const hasCharacter = allMapModel.some((m) => m.type === AssetModelType.CHARACTER);

  const handleSelectAsset = (slotIndex?: number) => (asset: AssetModel) => {
    play();
    dispatch(actions.updateActionMenu({ key: "asset", value: true, exclusive: true }));
    dispatch(actions.selectedAssetMesh(asset));
    dispatch(actions.selectSlot(slotIndex ?? null));
  };

  const handleSelectSlot = (index: number) => () => {
    dispatch(actions.selectSlot(index));
    if (!slot[index]) {
      play();
      dispatch(actions.updateActionMenu({ key: "assetList", value: true, exclusive: true }));
      dispatch(actions.selectedAssetMesh(null));
    }
  };

  const handleOpenAssetList = () => {
    play();
    // 空のスロットを探す
    let targetSlot = selectedSlotIndex;
    if (targetSlot === null || targetSlot === undefined) {
      targetSlot = slot.findIndex((s) => !s);
      if (targetSlot === -1) targetSlot = 0;
    }
    dispatch(actions.selectSlot(targetSlot));
    dispatch(
      actions.updateActionMenu({ key: "assetList", value: !showAssetList, exclusive: true }),
    );
  };

  return createPortal(
    <div
      ref={containerRef}
      className="flex-col-el absolute bottom-3 left-0 right-0 z-[10000] pointer-events-auto"
    >
      <AssetListModal />

      <div className="flex-row-el justify-center mx-auto">
        <div className="flex-row-el items-center gap-3 px-4 py-2 rounded-2xl bg-white/80 backdrop-blur-sm border-2 border-white shadow-lg">
          {/* 必須アセット: キャラ・ゴール */}
          <div className="flex-row-el items-center gap-2 pr-3 border-r-2 border-gray-200">
            <div style={{ width: itemSize, height: itemSize }} className="shrink-0">
              <AssetItem
                asset={character}
                selected={selectedAssetModel?.name === character.name}
                disabled={hasCharacter}
                onSelect={handleSelectAsset()}
              />
            </div>
            <div style={{ width: itemSize, height: itemSize }} className="shrink-0">
              <AssetItem
                asset={goal}
                selected={selectedAssetModel?.name === goal.name}
                disabled={false}
                onSelect={handleSelectAsset()}
              />
            </div>
          </div>

          {/* カスタムスロット */}
          <div className="flex-row-el items-center gap-2">
            {slot.map((slotAsset, index) => (
              <div
                key={`${slotAsset?.name}_${index}`}
                style={{ width: itemSize, height: itemSize }}
                className="shrink-0"
              >
                {slotAsset ? (
                  <AssetItem
                    asset={slotAsset}
                    selected={
                      selectedSlotIndex === index || selectedAssetModel?.name === slotAsset.name
                    }
                    onSelect={handleSelectAsset(index)}
                  />
                ) : (
                  <EmptySlot
                    selected={selectedSlotIndex === index}
                    onClick={handleSelectSlot(index)}
                  />
                )}
              </div>
            ))}
          </div>

          {/* 一覧ボタン */}
          <div className="flex-row-el items-center pl-3 border-l-2 border-gray-200">
            <button
              onClick={handleOpenAssetList}
              style={{ width: itemSize, height: itemSize }}
              className={`flex-col-el flex-center rounded-lg transition-all duration-150 active:scale-95 cursor-pointer
                ${
                  showAssetList
                    ? "bg-blue-100 border-2 border-blue-400"
                    : "bg-gray-100 hover:bg-gray-200 border-2 border-transparent"
                }`}
            >
              <AppsIcon
                className={showAssetList ? "text-blue-600" : "text-gray-600"}
                style={{ fontSize: 28 }}
              />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
